import { useRef, useState, useEffect } from "react";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { FieldControllerTypes, IFormController } from "../../form_v2/types";
import PersonIcon from "@mui/icons-material/Person";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import ContactsIcon from "@mui/icons-material/Contacts";
import { Form, Formik, FormikContextType } from "formik";
import FormLayout from "../../layout/Form/FormLayout";
import { VerticalStepper } from "../../Stepper";
import { formInStepperBuilder } from "../../form_v2/helpers";
import FormActionButtonLayout from "../../layout/Form/FormActionButtonLayout";
import { Button } from "@mui/material";
import {
  isSubmitEnabled,
  maxValidationString,
  minValidationString,
  requiredField,
} from "../../../lib/helperFuncs";
import DialogAction from "../../Dialog/DialogAction";
import { useNavigate } from "react-router-dom";
import { VisitorFormProps } from "./type";
import { matchIsValidTel } from "mui-tel-input";
import { AuthMethod } from "../../../lib/generalTypes";
import { IntExt } from "../../../lib/permissions";
import UserService from "../../../services/UserService";
import HttpService from "../../../services/HttpService";
import AsyncButton from "../../AsyncButton/AsyncButton";
import { useSnackbar } from "notistack";

const VisitorFormSteps = ({
  initialValues,
  onSubmit,
  categoryOptions,
  isSubmitLoading = false,
}: VisitorFormProps) => {
  const [openSubmit, setOpenSubmit] = useState<boolean>(false);
  const [openCancel, setOpenCancel] = useState<boolean>(false);
  const [keycloakRoles, setKeycloakRoles] = useState([]);

  const { enqueueSnackbar } = useSnackbar();

  const navigate = useNavigate();
  const { t } = useTranslation();
  const formRef = useRef<FormikContextType<any>>(null);

  const is_internal = UserService.hasRole([IntExt.Internal]);

  const validationSchema = Yup.object({
    // step 1
    first_name: Yup.string()
      .required(requiredField)
      .min(1, minValidationString(1))
      .max(40, maxValidationString(40)),
    last_name: Yup.string()
      .required(requiredField)
      .min(1, minValidationString(1))
      .max(40, maxValidationString(40)),
    // step 2
    email: Yup.string()
      .required(requiredField)
      .email("Μη αποδεκτή μορφή Email")
      .matches(
        /^\w+([.-]?\w+)*@\w+([-]?\w+)*(\.\w{2,5}(?!\.\w))$/,
        "Μη αποδεκτή μορφή Email"
      ),
    phone_number: Yup.string()
      .required(requiredField)
      .test("phone_number", "Λάθος μορφή Τηλεφώνου", (value) =>
        matchIsValidTel(value || "")
      ),
    //step3
    auth: Yup.string().required(requiredField),
  });

  const step1Form: IFormController[] = [
    ...(initialValues.auth === AuthMethod.EXTERNAL_TAXISNET &&
    initialValues.nomiki
      ? []
      : [
        {
          control: FieldControllerTypes.input,
          label: t("Όνομα"),
          name: "first_name",
          disabled: is_internal,
          required: true,
        },
      ]),
    {
      control: FieldControllerTypes.input,
      label:
        initialValues.auth === AuthMethod.EXTERNAL_TAXISNET &&
        initialValues.nomiki
          ? t("Επωνυμία")
          : t("Επώνυμο"),
      name: "last_name",
      disabled:
        (initialValues.auth === AuthMethod.EXTERNAL_TAXISNET &&
          initialValues.nomiki) ||
        is_internal,
      required: true,
    },
    ...(is_internal
      ? [
        {
          control: FieldControllerTypes.autocomplete,
          label: t("Κατηγορία"),
          name: "category",
          customLabel: true,
          options: categoryOptions,
        },
      ]
      : []),
  ];
  const step2Form: IFormController[] = [
    {
      control: FieldControllerTypes.input,
      label: "Εmail",
      name: "email",
      required: true,
      info: true,
      disabled: initialValues.auth === AuthMethod.BASIC_AUTH || is_internal,
      tooltip: t("To Email θα είναι και το username του Χρήστη"),
    },
    {
      control: FieldControllerTypes.telInput,
      label: t("Τηλέφωνο"),
      name: "phone_number",
      disabled: is_internal,
      required: true,
    },
  ];
  const [openModal, setOpenModal] = useState<boolean>(false);

  const step3Form: IFormController[] = [
    {
      control: FieldControllerTypes.input,
      label: t("Μέθοδος Πιστοποίησης"),
      name: "auth",
      disabled: true,
    },
    ...(is_internal
      ? [
        {
          control: FieldControllerTypes.autocomplete,
          label: t("Επιπλέον Ρόλοι"),
          name: "roles",
          options: keycloakRoles,
          customLabel: true,
          required: true,
        },
      ]
      : []),
  ];

  const step1 = {
    label: t("Στοιχεία Χρήστη"),
    content: step1Form,
    icon: <PersonIcon />,
  };

  const step2 = {
    label: t("Στοιχεία Επικοινωνίας"),
    content: step2Form,
    icon: <LocalPhoneIcon />,
  };
  const step3 = {
    label: t("Χαρακτηριστικά Εισόδου"),
    content: step3Form,
    icon: <ContactsIcon />,
  };

  const wholeForm = [{ ...step1 }, { ...step2 }, { ...step3 }];

  const getKeycloakCompositeRoles = async () => {
    const response = await HttpService.getAxiosClient().get(
      `${process.env.REACT_APP_BACKEND_URL}/users-parametrics/roles/composites`
    );
    setKeycloakRoles(
      response.data.map((x: any) => {
        return {
          key: x.name,
          value: x.id,
        };
      })
    );
  };

  useEffect(() => {
    is_internal && getKeycloakCompositeRoles();
  }, []);
  const onChangePasswordSubmit = async () => {
    try {
      const body = { id: initialValues.id };
      const response = await HttpService.getAxiosClient().post(
        `${process.env.REACT_APP_BACKEND_URL}/users/password`,
        body
      );
      if (response.status === 201) {
        enqueueSnackbar(response.data.message, { variant: "success" });
      } else {
        // Επειδη μερικές φορές laggarei και ερχονται τα emails από keycloak με καθυστέρηση (πχ 10 λεπτών).
        enqueueSnackbar(response.data.message, { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar(
        t("Παρουσιάστηκε σφάλμα, παρακαλούμε προσπαθήστε αργότερα."),
        { variant: "error" }
      );
      console.error(error);
    }
  };

  return (
    <>
      {/* <DialogAction
        open={openModal}
        handleClose={() => setOpenModal(false)}
        title={String(t("Αλλαγή Κωδικού"))}
        content={t(
          "Θα σας σταλεί ένα ενημερωτικό email για να αλλάξετε τον κωδικό σας. Είστε σίγουρος/η ότι θέλετε να προχωρήσετε;"
        )}
        onAccept={() => {
          onChangePasswordSubmit();
          setOpenModal(false);
        }}
      />

      <Box display="flex" mr={5} justifyContent="flex-end">
        <Button
          variant="contained"
          onClick={() => setOpenModal(true)}
          sx={{ "&:hover": { cursor: "pointer" } }}
        >
          {t("Αλλαγή Κωδικού")}
        </Button>
      </Box> */}

      <Formik
        onSubmit={() => undefined}
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnBlur={false}
        innerRef={formRef}
      >
        {(formik) => (
          <div>
            <Form>
              <FormLayout>
                <VerticalStepper activeStep={1}>
                  {formInStepperBuilder(wholeForm, formik, false)}
                </VerticalStepper>
              </FormLayout>
              <FormActionButtonLayout>
                <Button
                  variant='outlined'
                  color='error'
                  onClick={() => setOpenCancel(true)}
                >
                  {t("Ακύρωση")}
                </Button>
                <AsyncButton
                  isLoading={isSubmitLoading}
                  title={t("Αποθήκευση")}
                  disabled={
                    is_internal
                      ? false
                      : isSubmitEnabled(formik?.errors, formik?.dirty)
                  }
                  onClick={() => setOpenSubmit(true)}
                />
                <DialogAction
                  open={openSubmit}
                  handleClose={() => setOpenSubmit(false)}
                  title={`${t("Επεξεργασία")}`}
                  content={t(
                    "Είστε σίγουρος/η ότι θέλετε να αποθηκεύσετε τις αλλαγές των στοιχείων;"
                  )}
                  onAccept={() => {
                    setOpenSubmit(false);
                    onSubmit(formik);
                  }}
                />
                <DialogAction
                  open={openCancel}
                  handleClose={() => setOpenCancel(false)}
                  title={`${t("Ακύρωση Επεξεργασίας")}`}
                  content={t(
                    "Ειστε σιγουρος/η ότι θέλετε να ακυρώσετε τις αλλαγές των στοιχείων;"
                  )}
                  onAccept={() => {
                    setOpenCancel(false);
                    navigate(-1);
                  }}
                />
              </FormActionButtonLayout>
            </Form>
          </div>
        )}
      </Formik>
    </>
  );
};

export default VisitorFormSteps;
